import { SimpleUIService } from '../services/simple-ui/simple-ui.service';

/** A project constant entry from the project-version `defaultState`. */
export interface ConstantEntry {
  /** Current schema spelling of the variable name (wins over `name`). */
  key?: string;
  /** Legacy spelling used by the ehq-web project-constants UI. */
  name?: string;
  value?: unknown;
}

/** A workflow variable entry from NotebookVariablesService.variableList. */
export interface WorkflowVariableEntry {
  name?: string;
  value?: unknown;
  sortOrder?: number;
}

export interface BuildStepDataOptions {
  /**
   * Always include the `job_arguments` variable regardless of sortOrder
   * (Python path behavior since SCRUM-500; the JS path resolves job args
   * separately via getJobArguments).
   */
  alwaysIncludeJobArgs?: boolean;
}

/**
 * Builds the step-execution `data` map from project constants and workflow
 * variables (SCRUM-626). Mirrors enoch's headless merge
 * (`ehq_core::utils::default_state::merge_default_state_with_constants`):
 *
 * ```text
 * project constant  <  workflow variable
 * ```
 *
 * - Constant names accept both `key` and `name` spellings (`key` wins
 *   within an entry); nameless constant entries are skipped.
 * - A constant shadowed by ANY workflow variable name is dropped entirely —
 *   in enoch's merged default_state a shadowed constant never exists — even
 *   when the variable itself is excluded by sortOrder gating.
 * - Workflow variables keep the pre-existing inclusion rule: included once
 *   the step's sortOrder has reached them.
 * - With no (or malformed) constants the result is identical to the
 *   variables-only build.
 */
export class StepDataHelper {
  /** Variable name of a constant entry (`key` wins over legacy `name`). */
  static constantName(entry: ConstantEntry | null | undefined): string | undefined {
    const name = entry?.key ?? entry?.name;
    return typeof name === 'string' && name.length > 0 ? name : undefined;
  }

  static buildStepDataMap(
    constants: ConstantEntry[] | null | undefined,
    variables: WorkflowVariableEntry[] | null | undefined,
    chunkSortOrder: number | null,
    opts: BuildStepDataOptions = {},
  ): Record<string, unknown> {
    const data: Record<string, unknown> = {};
    const variableList = Array.isArray(variables) ? variables : [];

    // Shadow set: ALL workflow variable names, regardless of sortOrder.
    const variableNames = new Set(
      variableList
        .map((v) => v.name)
        .filter((n): n is string => typeof n === 'string'),
    );

    // Lay down unshadowed constants first…
    if (Array.isArray(constants)) {
      for (const entry of constants) {
        const name = StepDataHelper.constantName(entry);
        if (!name || variableNames.has(name)) {
          continue;
        }
        data[name] = entry.value;
      }
    }

    // …then overlay workflow variables (workflow wins collisions).
    for (const element of variableList) {
      if (typeof element.name !== 'string') {
        continue;
      }
      const isJobArgsVar =
        !!opts.alwaysIncludeJobArgs &&
        SimpleUIService.isJobArgumentsVariableName(element.name);
      const reached =
        chunkSortOrder != null &&
        element.sortOrder != null &&
        chunkSortOrder >= element.sortOrder;
      if (isJobArgsVar || reached) {
        data[element.name] = element.value;
      }
    }

    return data;
  }
}
